import React from 'react';
import { Link } from 'react-router-dom';
import useFadeIn from '../hooks/useFadeIn';
import { buildSignupPath, buildSignupState, captureCtaClick } from '../utils/analytics';

const newsItems = [
  {
    date: 'April 2026',
    category: 'Publication',
    categoryColor: 'bg-blue-100 text-blue-700',
    title: 'MedHug Publishes Its First Peer-Reviewed Paper in AJHP',
    description:
      'A milestone for our team — our inaugural research on Medicare Part D and the Inflation Reduction Act is now published in the American Journal of Health-System Pharmacy, one of the field\'s most respected journals.',
    links: [
      {
        label: 'Read Article',
        href: 'https://academic.oup.com/ajhp/advance-article/doi/10.1093/ajhp/zxag083/8512908?utm_source=authortollfreelink&utm_campaign=ajhp&utm_medium=email&guestAccessKey=8abb715e-259a-4457-894c-97ec93cf0512',
        primary: true,
      },
      {
        label: 'Download PDF',
        href: '/medhug-ira-paper.pdf',
        primary: false,
      },
    ],
  },
  {
    date: 'Nov 2025',
    category: 'Event',
    categoryColor: 'bg-purple-100 text-purple-700',
    title: "Dr. Michael Blyumin to Present MedHug\u2019s Work at CSHP Seminar 2025",
    description:
      'MedHug advisor Dr. Michael Blyumin has been invited to present at the California Society of Health-System Pharmacists (CSHP) Seminar 2025 — bringing our mission to improve prescription access to a national audience of pharmacy leaders.',
    links: [
      {
        label: 'View on LinkedIn',
        href: 'https://www.linkedin.com/posts/mgblyumin_cshpseminar2025-activity-7365789573064396800-7F_W?utm_source=share&utm_medium=member_desktop',
        primary: true,
      },
    ],
  },
];

export default function NewsPage() {
  const [heroRef, heroVisible] = useFadeIn(0.05);
  const [listRef, listVisible] = useFadeIn(0.08);

  return (
    <div className="bg-white">

      {/* ── Hero ──────────────────────────────────── */}
      <section className="bg-[#f5f5f7] px-8 md:px-16 pt-14 pb-8">
        <div
          ref={heroRef}
          className={`fade-in-section ${heroVisible ? 'is-visible' : ''} max-w-[860px] mx-auto`}
        >
          <p className="text-[13px] text-[#86868b] tracking-widest uppercase font-medium mb-5">
            Latest News
          </p>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1d1d1f] tracking-tight leading-tight mb-4">
            Updates from MedHug
          </h1>
          <p className="text-lg text-[#86868b] leading-relaxed font-light whitespace-nowrap">
            Research, events, and milestones as we build the future of prescription access.
          </p>
        </div>
      </section>

      {/* ── News List ─────────────────────────────── */}
      <section
        ref={listRef}
        className={`fade-in-section ${listVisible ? 'is-visible' : ''} px-8 md:px-16 py-12 max-w-[860px] mx-auto`}
      >
        <div className="divide-y divide-[#e5e5ea]">
          {newsItems.map((item, i) => (
            <article key={i} className="py-8 group">
              {/* Meta row */}
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${item.categoryColor}`}>
                  {item.category}
                </span>
                <span className="text-xs text-[#86868b]">{item.date}</span>
              </div>

              {/* Title */}
              <h2 className="text-xl md:text-[22px] font-semibold text-[#1d1d1f] leading-snug mb-2">
                {item.title}
              </h2>

              {/* Description */}
              <p className="text-[15px] text-[#86868b] leading-relaxed mb-4">
                {item.description}
              </p>

              {/* Links */}
              <div className="flex flex-wrap items-center gap-5">
                {item.links.map((link) =>
                  link.primary ? (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[14px] font-medium text-[#06c] hover:text-[#004aad] transition-colors"
                    >
                      {link.label}
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M7 17L17 7M17 7H7M17 7v10"/>
                      </svg>
                    </a>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#86868b] hover:text-[#1d1d1f] transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                      </svg>
                      {link.label}
                    </a>
                  )
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── CTA strip ─────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#1d1d1f] px-8 md:px-16 py-12">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-blue-600/15 via-purple-600/10 to-transparent blur-3xl pointer-events-none" />
        <div className="relative max-w-[860px] mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Want to see MedHug in action?
            </h3>
            <p className="text-[15px] text-white/50 font-light">
              Book a 30-minute demo with our team.
            </p>
          </div>
          <Link
            to={buildSignupPath()}
            state={buildSignupState('news-book-demo')}
            data-ph-capture-attribute-cta="news-book-demo"
            onClick={() => captureCtaClick('news-book-demo', { placement: 'news-cta' })}
            className="bg-white text-[#1d1d1f] px-8 py-4 rounded-full text-[15px] font-medium hover:bg-gray-100 transition-all whitespace-nowrap flex-shrink-0 shadow-lg"
          >
            Book a Demo
          </Link>
        </div>
      </section>

    </div>
  );
}
